import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BaseService } from './base.service';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { SupplierAddressPropertyValues, SupplierPropertyValue, TabPermissions, SupplierEntityPropertyValues, FieldConfig, FieldConfiguration, SupplierAddress, SupplierDocumentPropertyValues, SupplierBankPropertyValue, BankConfiguration, SupplierProfileEvent, BankSecurityValidationResponse, BankValidationRequest, SupplierDiversityPropertyValue, SupplierTaxPropertyValues, SupplierEhsResponsePropertyValue, EmailDomainPropertyValue } from '../models/supplier.model';
import { KeyValuePair } from '../models/generic.model';
import { LocalStorageService } from '../../shared/services/index.service';
import { OTP_TOKEN } from '../../shared/services/otp/otp.constants';
import { BANK_TOKEN } from '../../shared/constants';
import { AppSettings } from '../../app.settings';
import { SupplierTaskEntityMapping } from '../models/suppliertasks.model';

@Injectable()
export class SupplierStateService extends BaseService {
    constructor(private http: HttpClient,
        private localStorage: LocalStorageService, private config: AppSettings) {
        super();
    }

    private setMultiPartHeaders() {
        const headersConfig = {
            "auth-token": "token"
        };
    }

    getDomainListByCode(SupplierCode: string): Observable<EmailDomainPropertyValue[]> {
        const url = `${this.config.BaseUrl}Profile/supplierinformation/${SupplierCode}/domain`
        return this.http
            .get<SupplierPropertyValue>(url)
            .pipe(
                map(res => {
                    return res && res.domains ? res.domains : null;
                }));
    }

    getAddressListByCode(SupplierCode: string): Observable<SupplierAddressPropertyValues[]> {
        const url = `${this.config.BaseUrl}Profile/supplierinformation/${SupplierCode}/address`
        return this.http
            .get<SupplierPropertyValue>(url)
            .pipe(
                map(res => {
                    return res && res.addresses ? res.addresses : null;
                }));
    }

    getTabPermissions(SupplierCode: string): Observable<TabPermissions> {
        const url = `${this.config.BaseUrl}Profile/GetTabLevelPermissions?supplierCode=${SupplierCode}`;
        return this.http.get<TabPermissions>(url);
    }

    getEntityListByCode(SupplierCode: string): Observable<SupplierEntityPropertyValues[]> {
        const url = `${this.config.BaseUrl}Profile/supplierinformation/${SupplierCode}/entity`
        return this.http.get<SupplierPropertyValue>(url)
            .pipe(
                map(res => {
                    return res ? res.entities : null;
                }));
    }

    getFieldConfig(section: string, countryCode: string, entityName: string, subcategory: number = -1): Observable<any> {
        let params = new HttpParams();
        params = params.set('section', section);
        if (countryCode) {
            params = params.set('countryCode', countryCode.toString());
        }
        if (entityName) {
            params = params.set('entityCode', entityName);
        }

        if (subcategory) {
            params = params.set('supplierSubCategoryId', `${subcategory}`);
        }

        return this.http.get<any>(`${this.config.BaseUrl}Profile/FieldConfig`, { params: params })
            .pipe(
                map((event: FieldConfiguration[]) => {
                    if (event && event.find(x => x.name == section)) return event.find(x => x.name == section).fields.map(x => {
                        return x;
                    });
                }));
    }

    getPredictions(address: SupplierAddress): Observable<any[]> {
        let params = new HttpParams();
        params = params.set('address', address.Street1);
        params = params.set('state', address.State);
        params = params.set('city', address.City);
        params = params.set('state', address.State);
        params = params.set('zip', address.ZipCode);
        params = params.set('country', address.CountryCode);
        return this.http.get<any[]>(`${this.config.BaseUrl}Profile/Masterdata/GetAddressPredictions`, { params: params });
    }

    uploadFile(data: FormData): Observable<number> {
        const url = `${this.config.BaseUrl}Profile/UploadDocument`;
        return this.http.post<number>(url, data, { headers: this.setFileHeaders() });
    }

    getDocumentsListByCode(SupplierCode: string): Observable<SupplierDocumentPropertyValues[]> {
        const url = `${this.config.BaseUrl}Profile/supplierinformation/${SupplierCode}/document`
        return this.http
            .get<SupplierPropertyValue>(url)
            .pipe(
                map(res => {
                    return res && res.documents ? res.documents : null;
                }));
    }

    downloadFile(supplierCode: string, referenceSystem: string, documentId: string, version: string): Observable<Blob> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/downloadDocument/${referenceSystem}/${documentId}?version=${version}`;
        return this.http.get<Blob>(url, { headers: this.setFileHeaders(), responseType: "Blob" as "json" });
    }

    downloadFileFromClm(supplierCode: string, contractID: number): Observable<Blob> {
        const url = `${this.config.BaseUrl}Profile/Document/${supplierCode}/downloadDocument/${contractID}`;
        return this.http.get<Blob>(url, { headers: this.setFileHeaders(), responseType: "Blob" as "json" });
    }

    getBankListByCode(SupplierCode: string): Observable<SupplierBankPropertyValue[]> {
        let headers = this.setHeaders();
        if (this.localStorage.retrieve(OTP_TOKEN)) {
            headers = headers.set(OTP_TOKEN, this.localStorage.retrieve(OTP_TOKEN));
        }

        const url = `${this.config.BaseUrl}Profile/supplierinformation/${SupplierCode}/bank`
        return this.http
            .get<SupplierPropertyValue>(url, { headers })
            .pipe(
                map(res => {
                    return res && res.banks ? res.banks : null;
                }));
    }

    getTaxListByCode(SupplierCode: string): Observable<SupplierTaxPropertyValues[]> {
        let headers = this.setHeaders();
        if (this.localStorage.retrieve(OTP_TOKEN)) {
            headers = headers.set(OTP_TOKEN, this.localStorage.retrieve(OTP_TOKEN));
        }

        const url = `${this.config.BaseUrl}Profile/supplierinformation/${SupplierCode}/tax`
        return this.http
            .get<SupplierPropertyValue>(url, { headers })
            .pipe(
                map(res => {
                    return res && res.taxes ? res.taxes : null;
                }));
    }

    getDiversityByCode(supplierCode: string): Observable<SupplierDiversityPropertyValue> {
        const url = `${this.config.BaseUrl}Profile/supplierinformation/${supplierCode}/Diversity`
        let headers = this.setHeaders();
        
        return this.http
            .get<SupplierPropertyValue>(url, { headers })
            .pipe(
                map(res => {
                    return res && res.diversity ? res.diversity : null;
                }));
    }

    getEhsResponseByCode(supplierCode: string): Observable<SupplierEhsResponsePropertyValue[]> {
        const url = `${this.config.BaseUrl}Profile/supplierinformation/${supplierCode}/EHS`
        let headers = this.setHeaders();
        
        return this.http
            .get<SupplierPropertyValue>(url, { headers })
            .pipe(
                map(res => {
                    return res && res.ehsResponse ? res.ehsResponse : null;
                }));
    }

    getBankConfiguration(countryCode: string, paymentOrigin: string): Observable<BankConfiguration> {
        let headers = this.setHeaders();
        if (this.localStorage.retrieve(OTP_TOKEN)) {
            headers = headers.set(OTP_TOKEN, this.localStorage.retrieve(OTP_TOKEN));
        }
        return this.http.get<BankConfiguration>(`${this.config.BaseUrl}Profile/Bank/countryCode/${countryCode}/paymentOrigin/${paymentOrigin}/BankConfiguration`, { headers });
    }

    /** Gets bank configuration using supplier code and country code */
    getBankConfigurationForOnboarding(supplierCode: string, countryCode: string, requestId: string) {
        let headers = this.setHeaders();
        //  No need to include OTP token for onboarding
        return this.http.get<BankConfiguration>(`${this.config.BaseUrl}Profile/Bank/${supplierCode}/countryCode/${countryCode}/requestId/${requestId}/BankConfigurationForOnboarding`, { headers });
    }

    validateBankConfiguration(supplierCode: string, bankConfig: BankValidationRequest): Observable<string[]> {
        const url = `${this.config.BaseUrl}Profile/Bank/${supplierCode}/ValidateBankInformation`;
        let headers = this.setHeaders();
        if (this.localStorage.retrieve(OTP_TOKEN)) {
            headers = headers.set(OTP_TOKEN, this.localStorage.retrieve(OTP_TOKEN));
        }
        return this.http.post<string[]>(url, bankConfig, { headers });
    }

    /** Sends validation requestion without OTP token */
    validateBankConfigurationWithoutOTP(supplierCode: string, bankConfig: BankValidationRequest) {
        const url = `${this.config.BaseUrl}Profile/Bank/${supplierCode}/ValidateBankInformationWithoutOTP`;
        let headers = this.setHeaders();
        //  No need to include OTP token
        return this.http.post<string[]>(url, bankConfig, { headers });
    }

    upsertBankSecurityAnswers(supplierCode: string, data: SupplierProfileEvent): Observable<BankSecurityValidationResponse> {
        const url = `${this.config.BaseUrl}Profile/Bank/${supplierCode}/UpsertBankSecurityAnswers`;
        let headers = this.setHeaders();
        if (this.localStorage.retrieve(OTP_TOKEN)) {
            headers = headers.set(OTP_TOKEN, this.localStorage.retrieve(OTP_TOKEN));
        }

        return this.http.post<BankSecurityValidationResponse>(url, data, { headers });
    }

    getPaymentOrigins(countryCode: string): Observable<KeyValuePair<string, string>[]> {
        let headers = this.setHeaders();
        if (this.localStorage.retrieve(OTP_TOKEN)) {
            headers = headers.set(OTP_TOKEN, this.localStorage.retrieve(OTP_TOKEN));
        }
        return this.http.get<any>(`${this.config.BaseUrl}Profile/Bank/${countryCode}/BankPaymentOrigins`, { headers })
            .pipe(
                map((errors: any) => {
                    if (errors) return errors.map(y => {
                        const a = new KeyValuePair<string, string>();
                        a.Key = y.key;
                        a.Value = y.value;
                        return a;
                    });
                }));
    }

    getEntityMappingByCodeAndTaskType(supplierCode: string, taskType): Observable<SupplierTaskEntityMapping[]> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/entityDetails/${taskType}`
        return this.http
            .get<SupplierTaskEntityMapping[]>(url)
            .pipe(
                map(res => {
                    return res || null;
                }));
    }
}